import { ArrowLeft, ClipboardList, FileJson, Gauge, Package, Play, TableProperties } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { IntegrationJob, IntegrationRun } from "../../shared/types";
import { useAuthUser } from "../auth/AuthProvider";
import { PayloadModal } from "../components/common/PayloadModal";
import { SeverityBadge } from "../components/common/SeverityBadge";
import { StateBlock } from "../components/common/StateBlock";
import { StatusChip } from "../components/common/StatusChip";
import { getJob } from "../services/jobService";
import { rerunAcrJob } from "../services/rerunService";
import { getRunsForJob } from "../services/runService";
import { formatDateTime, formatDuration, getStatusSummary, statusLabels } from "../utils/status";

interface JobDetailPageProps {
  jobId?: string;
  refreshKey: number;
  onBack: () => void;
}

export function JobDetailPage({ jobId, refreshKey, onBack }: JobDetailPageProps) {
  const [job, setJob] = useState<IntegrationJob>();
  const [runs, setRuns] = useState<IntegrationRun[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(jobId));
  const [error, setError] = useState<string>();
  const [payloadPath, setPayloadPath] = useState<string>();
  const [rerunMessage, setRerunMessage] = useState<string>();
  const [isRerunning, setIsRerunning] = useState(false);
  const { hasAnyRole } = useAuthUser();

  useEffect(() => {
    if (!jobId) {
      setJob(undefined);
      setRuns([]);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(undefined);

    Promise.all([getJob(jobId), getRunsForJob(jobId)])
      .then(([nextJob, nextRuns]) => {
        if (!isMounted) {
          return;
        }
        setJob(nextJob);
        setRuns(nextRuns);
      })
      .catch((err: Error) => {
        if (isMounted) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [jobId, refreshKey]);

  const latestRun = runs[0];
  const rawPayloadPath = latestRun?.rawPayloadPath || job?.rawPayloadPath;
  const isSynapseJob = job?.sourceType === "SYNAPSE";
  const isAcrJob = job?.sourceType === "ACR";
  const hasRerunRole = hasAnyRole(["HealthDashboard.Operator", "HealthDashboard.Admin"]);
  const isRerunnableAcrStatus = Boolean(job && ["FAILED", "MISSING", "LATE", "LONG_RUNNING"].includes(job.status));
  const canRerunAcrJob = Boolean(
    job?.id &&
      isAcrJob &&
      hasRerunRole &&
      isRerunnableAcrStatus
  );
  const rerunUnavailableReason = !hasRerunRole
    ? "Operator or Admin role is required. Sign out and back in if your role was recently changed."
    : job?.status === "RUNNING"
      ? "This job is already running."
      : "Rerun is available for failed, missing, late, or long-running ACR jobs.";
  const durationTrend = useMemo(
    () => runs.filter((run) => run.durationMinutes !== undefined).slice(0, 7).reverse(),
    [runs]
  );
  const failureHistory = useMemo(
    () => runs.filter((run) => run.status === "FAILED" || run.status === "MISSING" || run.status === "LONG_RUNNING").slice(0, 5),
    [runs]
  );

  const stats = useMemo(() => {
    const lastSeven = runs.slice(0, 7);
    const completed = lastSeven.filter((run) => ["SUCCEEDED", "FAILED", "CANCELLED", "LATE"].includes(run.status));
    const successful = lastSeven.filter((run) => run.status === "SUCCEEDED" || run.status === "LATE");
    const durations = runs.map((run) => run.durationMinutes).filter((value): value is number => value !== undefined);
    const averageDuration =
      durations.length === 0 ? undefined : Math.round(durations.reduce((total, value) => total + value, 0) / durations.length);

    return {
      successRate: completed.length === 0 ? "0%" : `${Math.round((successful.length / completed.length) * 100)}%`,
      averageDuration,
      failureCount: runs.filter((run) => run.status === "FAILED").length
    };
  }, [runs]);

  if (!jobId) {
    return <StateBlock type="empty" title="No job selected" message="Open a job from the runs table or dashboard incident list." />;
  }

  if (isLoading) {
    return <StateBlock type="loading" title="Loading job detail" message="Retrieving job configuration and recent runs." />;
  }

  if (error || !job) {
    return <StateBlock type="error" title="Job detail unavailable" message={error || "The selected job was not found."} />;
  }

  async function requestAcrRerun() {
    if (!job?.id || isRerunning) {
      return;
    }

    const confirmed = window.confirm(`Start a new Container Apps job execution for ${job.name}?`);
    if (!confirmed) {
      return;
    }

    setIsRerunning(true);
    setRerunMessage(undefined);
    try {
      const result = await rerunAcrJob(job.id);
      setRerunMessage(`Rerun requested for ${result.containerAppJobName}.`);
    } catch (err) {
      setRerunMessage(err instanceof Error ? err.message : "Unable to request ACR job rerun.");
    } finally {
      setIsRerunning(false);
    }
  }

  return (
    <section className="page-stack">
      <div className="page-heading">
        <button className="ghost-button" type="button" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" />
          Back
        </button>
        <div className="job-title-block">
          <p>{job.sourceType} - {job.sourceName}</p>
          <h2>{job.name}</h2>
        </div>
        <StatusChip status={job.status} />
      </div>

      <div className="detail-grid">
        <section className="panel detail-overview">
          <div className="panel-heading">
            <div>
              <p>Job overview</p>
              <h3>Current State</h3>
            </div>
            <ClipboardList size={18} aria-hidden="true" />
          </div>
          <div className="overview-grid">
            {job.statusReason && <div><span>Status reason</span><strong>{job.statusReason}</strong></div>}
            <div><span>Current status</span><strong>{job.statusReason ? statusLabels[job.status] : getStatusSummary(job.status)}</strong></div>
            <div><span>Expected schedule</span><strong>{job.expectedStartTime}</strong></div>
            <div><span>Grace period</span><strong>{job.graceMinutes} minutes</strong></div>
            <div><span>Expected duration</span><strong>{job.expectedDurationMinutes} minutes</strong></div>
            <div><span>Owner</span><strong>{job.owner}</strong></div>
            <div><span>Severity</span><SeverityBadge severity={job.severity} /></div>
          </div>
        </section>

        <section className="panel stat-strip">
          <div>
            <span>Last 7 days success rate</span>
            <strong>{stats.successRate}</strong>
          </div>
          <div>
            <span>Average duration</span>
            <strong>{formatDuration(stats.averageDuration)}</strong>
          </div>
          <div>
            <span>Failure count</span>
            <strong>{stats.failureCount}</strong>
          </div>
        </section>

        {isSynapseJob && (
          <section className="panel synapse-detail-panel">
            <div className="panel-heading">
              <div>
                <p>Synapse pipeline run detail</p>
                <h3>{latestRun?.pipelineName || job.pipelineName || job.name}</h3>
              </div>
              <StatusChip status={latestRun?.status || job.status} />
            </div>
            <div className="overview-grid">
              <div><span>Workspace</span><strong>{latestRun?.workspaceName || job.workspaceName || job.sourceName}</strong></div>
              <div><span>Pipeline</span><strong>{latestRun?.pipelineName || job.pipelineName || job.name}</strong></div>
              <div><span>External run id</span><strong>{latestRun?.externalRunId || latestRun?.id || "-"}</strong></div>
              <div><span>Run group</span><strong>{latestRun?.runGroupId || "-"}</strong></div>
              <div><span>Invoked by</span><strong>{latestRun?.invokedBy || "-"}</strong></div>
              <div><span>Last poll</span><strong>{formatDateTime(latestRun?.lastCheckedAt)}</strong></div>
            </div>
          </section>
        )}

        {isAcrJob && (
          <section className="panel acr-detail-panel">
            <div className="panel-heading">
              <div>
                <p>ACR job execution detail</p>
                <h3>{latestRun?.pipelineName || job.pipelineName || job.name}</h3>
              </div>
              <Package size={18} aria-hidden="true" />
            </div>
            <div className="overview-grid">
              <div><span>Source</span><strong>{latestRun?.sourceName || job.sourceName}</strong></div>
              <div><span>Job</span><strong>{latestRun?.pipelineName || job.pipelineName || job.name}</strong></div>
              <div><span>Repository</span><strong>{latestRun?.triggerName || job.triggerName || "-"}</strong></div>
              <div><span>External run id</span><strong>{latestRun?.externalRunId || latestRun?.id || "-"}</strong></div>
              <div><span>Last poll</span><strong>{formatDateTime(latestRun?.lastCheckedAt)}</strong></div>
              <div><span>Last error</span><strong>{latestRun?.lastError || "-"}</strong></div>
            </div>
            {isAcrJob && (
              <div className="job-action-card">
                <div>
                  <span>Operator action</span>
                  <strong>Start a new Container Apps job execution.</strong>
                </div>
                <button className="primary-button" type="button" disabled={!canRerunAcrJob || isRerunning} onClick={requestAcrRerun}>
                  <Play size={15} aria-hidden="true" />
                  {isRerunning ? "Requesting..." : "Rerun Job"}
                </button>
                <small>{rerunMessage || (canRerunAcrJob ? "Available for the current incident state." : rerunUnavailableReason)}</small>
              </div>
            )}
          </section>
        )}

        <section className="panel">
          <div className="panel-heading">
            <div>
              <p>Recent runs</p>
              <h3>Execution History</h3>
            </div>
            <TableProperties size={18} aria-hidden="true" />
          </div>
          <div className="table-scroll">
            <table className="data-table compact">
              <thead>
                <tr>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>End</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td>{formatDateTime(run.expectedStartTime)}</td>
                    <td>{formatDateTime(run.actualStartTime)}</td>
                    <td>{formatDateTime(run.endTime)}</td>
                    <td>{formatDuration(run.durationMinutes)}</td>
                    <td><StatusChip status={run.status} /></td>
                    <td className="error-cell">{run.lastError || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {isSynapseJob && (
          <section className="panel synapse-trend-panel">
            <div className="panel-heading">
              <div>
                <p>Synapse duration trend</p>
                <h3>Recent Duration</h3>
              </div>
            </div>
            <div className="duration-bars">
              {durationTrend.map((run) => (
                <div className="duration-bar" key={run.id}>
                  <span style={{ height: `${Math.max(12, Math.min(100, (run.durationMinutes || 0) * 2))}%` }} />
                  <small>{run.durationMinutes}m</small>
                </div>
              ))}
            </div>
            <div className="failure-history">
              <span>Failure History</span>
              {failureHistory.length === 0 ? (
                <strong>No recent Synapse failures.</strong>
              ) : (
                failureHistory.map((run) => (
                  <article key={run.id}>
                    <StatusChip status={run.status} />
                    <small>{formatDateTime(run.lastCheckedAt)} - {run.lastError || "No error message captured."}</small>
                  </article>
                ))
              )}
            </div>
          </section>
        )}

        <section className="panel">
          <div className="panel-heading">
            <div>
              <p>Run timeline</p>
              <h3>Status Progression</h3>
            </div>
            <Gauge size={18} aria-hidden="true" />
          </div>
          <div className="timeline-list large">
            {runs.map((run) => (
              <article className="timeline-row static" key={run.id}>
                <i className={`timeline-dot status-${run.status.toLowerCase().replace("_", "-")}`} />
                <span>
                  <strong>{formatDateTime(run.lastCheckedAt)} - {run.status}</strong>
                  <small>{run.lastError || `${run.jobName}: recorded execution status ${run.status}.`}</small>
                </span>
              </article>
            ))}
          </div>
        </section>

        <section className="panel raw-panel">
          <div className="panel-heading">
            <div>
              <p>Raw payload viewer</p>
              <h3>ADLS Raw Audit Payload</h3>
            </div>
          </div>
          <div className="payload-card">
            <FileJson size={24} aria-hidden="true" />
            <div>
              <span>RawPayloadPath</span>
              <strong>{rawPayloadPath || "No payload path recorded for this job."}</strong>
            </div>
            <button
              className="primary-button"
              type="button"
              onClick={() => rawPayloadPath && setPayloadPath(rawPayloadPath)}
              disabled={!rawPayloadPath}
            >
              <FileJson size={16} aria-hidden="true" />
              Open Payload
            </button>
          </div>
        </section>

        {latestRun?.lastError && (
          <section className="panel error-panel">
            <span>Last error message</span>
            <strong>{latestRun.lastError}</strong>
          </section>
        )}
        {payloadPath && <PayloadModal path={payloadPath} onClose={() => setPayloadPath(undefined)} />}
      </div>
    </section>
  );
}
